import type { ColDef } from "ag-grid-community";
import { StatusAgeCell } from "./components/StatusAgeCell";

/**
 * Pipeline column presets. Every column always exists in the grid (hidden
 * when not in the view) so the details panel can format any field and the
 * live flash can target any cell.
 */
export type PipelineViewId =
  "meeting" | "economics" | "structure" | "full" | "custom";

export type PipelineView = {
  id: PipelineViewId;
  label: string;
  description: string;
  /** Visible columns, in order. `custom` uses the user's own list. */
  fields: string[];
  /** Columns share the screen width (no horizontal scroll); `full` keeps fixed widths. */
  fitToWidth: boolean;
  /** Two-line rows for the stacked Meeting columns. */
  tallRows?: boolean;
};

// Rows are grouped under status bands; the Status column stays so a status
// can be changed during the meeting.
export const MEETING_FIELDS = [
  "deal_name",
  "status",
  "meeting_profile",
  "meeting_amounts",
  "meeting_terms",
  "meeting_returns",
  "meeting_dates",
  "meeting_outlook",
  "meeting_team",
];

export const PIPELINE_VIEWS: Record<PipelineViewId, PipelineView> = {
  meeting: {
    id: "meeting",
    label: "Meeting",
    description: "Grouped by status with totals, and stale deals marked",
    fields: MEETING_FIELDS,
    fitToWidth: true,
    tallRows: true,
  },
  economics: {
    id: "economics",
    label: "Economics",
    description: "Amounts, pricing, fees and returns",
    fields: [
      "deal_name",
      "status",
      "loan_amount_currency",
      "loan_amount_day_one",
      "loan_amount_fully_funded",
      "loan_amount_fully_funded_gbp",
      "margin",
      "arrangement_fee",
      "exit_fee",
      "commitment_fee",
      "extension_fee",
      "facility_agent_fee",
      "rate_floor",
      "irr_local",
      "irr_usd",
      "irr_local_net",
      "irr_usd_net",
      "wal",
      "ltv",
      "ltc",
    ],
    fitToWidth: true,
  },
  structure: {
    id: "structure",
    label: "Structure & covenants",
    description: "Loan terms, structure, covenants and flags",
    fields: [
      "deal_name",
      "status",
      "loan_type",
      "security_type",
      "transaction_type",
      "risk_class",
      "cm_rating",
      "initial_loan_term",
      "full_loan_term",
      "make_whole_fee_interestmonths",
      "equity_contribution",
      "amortisation",
      "amortisation_percentage",
      "amortisation_amount",
      "covenant_ltv",
      "covenant_ltc",
      "covenant_icr",
      "covenant_dscr",
      "covenant_dy",
      "is_portfolio",
      "is_development_loan",
    ],
    fitToWidth: true,
  },
  full: {
    id: "full",
    label: "All columns",
    description: "Every column, sized to fit the screen",
    fields: [],
    fitToWidth: true,
  },
  custom: {
    id: "custom",
    label: "Custom",
    description: "Your own columns (click again to choose them)",
    fields: [],
    fitToWidth: true,
  },
};

export const PIPELINE_VIEW_ORDER: PipelineViewId[] = [
  "meeting",
  "economics",
  "structure",
  "full",
  "custom",
];

export const isPipelineViewId = (value: unknown): value is PipelineViewId =>
  typeof value === "string" && value in PIPELINE_VIEWS;

const colKey = (col: ColDef) => col.colId ?? col.field ?? "";

/** Fields of the view that are actually shown (custom falls back to Meeting). */
export function visibleFieldsForView(
  viewId: PipelineViewId,
  allColumns: ColDef[],
  customFields: string[],
): string[] {
  const known = new Set(allColumns.map(colKey));
  if (viewId === "full") {
    return allColumns.map(colKey).filter((key) => !key.startsWith("meeting_"));
  }
  const fields =
    viewId === "custom"
      ? customFields.length > 0
        ? customFields
        : MEETING_FIELDS
      : PIPELINE_VIEWS[viewId].fields;
  return fields.filter((field) => known.has(field));
}

/**
 * Column defs for a view: its columns first (in view order, visible), every
 * other column after them hidden. Fitted views share the width with `flex`
 * (proportional to each column's normal width) so nothing scrolls sideways.
 */
export function applyPipelineView(
  allColumns: ColDef[],
  viewId: PipelineViewId,
  customFields: string[],
): ColDef[] {
  const view = PIPELINE_VIEWS[viewId];
  const visible = visibleFieldsForView(viewId, allColumns, customFields);
  const byKey = new Map(allColumns.map((col) => [colKey(col), col]));
  const visibleSet = new Set(visible);

  const shown = visible.map((key) => {
    const base = byKey.get(key)!;
    // The Meeting view's Status cell also shows days in that status.
    const col: ColDef =
      viewId === "meeting" && key === "status"
        ? { ...base, headerName: "Status (days)", cellRenderer: StatusAgeCell }
        : base;
    if (!view.fitToWidth) return { ...col, hide: false };
    const width = typeof col.width === "number" ? col.width : 120;
    if (col.pinned) {
      // Pinned columns don't flex; keep the name column a fixed width.
      return { ...col, hide: false };
    }
    return {
      ...col,
      hide: false,
      flex: width,
      minWidth: Math.min(width, 70),
      wrapHeaderText: true,
      autoHeaderHeight: true,
    };
  });
  const hidden = allColumns
    .filter((col) => !visibleSet.has(colKey(col)))
    .map((col) => ({ ...col, hide: true }));

  return [...shown, ...hidden];
}
